import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  useCreate,
  useDelete,
  useGetList,
  useNotify,
  useRefresh,
  useUpdate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

const inputClass =
  "h-8 rounded-md border border-input bg-transparent px-2 text-[12.5px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

interface RoutingRule {
  id: string;
  entity: "contact" | "deal";
  match: {
    city?: string;
    source?: string;
    service_type?: string;
    min_amount?: number;
  };
  assign_mode: "user" | "round_robin";
  assigned_to: number | null;
  pool: number[];
  position: number;
  active: boolean;
}

interface SalesUser {
  id: number;
  first_name: string;
  last_name: string;
}

const matchSummary = (rule: RoutingRule) => {
  const parts: string[] = [];
  if (rule.match.city) parts.push(`city is ${rule.match.city}`);
  if (rule.match.source) parts.push(`source is ${rule.match.source}`);
  if (rule.match.service_type)
    parts.push(`service is ${rule.match.service_type}`);
  if (rule.match.min_amount)
    parts.push(`value is at least $${rule.match.min_amount.toLocaleString()}`);
  return parts.length ? parts.join(" and ") : "every new one";
};

/**
 * Lead routing rules: who gets a NEW contact or deal that arrives without
 * an owner. First matching rule wins; an explicitly assigned owner always
 * beats routing. Enforcement lives in a database trigger, so form
 * submissions, imports and API writes all route identically.
 */
export const LeadRoutingSettings = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate();
  const [update] = useUpdate();
  const [deleteOne] = useDelete();

  const { data: rules } = useGetList<RoutingRule>("lead_routing_rules", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "position", order: "ASC" },
  });
  const { data: users } = useGetList<SalesUser>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });

  const [entity, setEntity] = useState<"contact" | "deal">("contact");
  const [city, setCity] = useState("");
  const [source, setSource] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [mode, setMode] = useState<"user" | "round_robin">("round_robin");
  const [userId, setUserId] = useState("");
  const [pool, setPool] = useState<number[]>([]);

  const userName = (id: number | null) => {
    const u = (users ?? []).find((x) => x.id === id);
    return u ? `${u.first_name} ${u.last_name}` : `#${id ?? "?"}`;
  };

  const addRule = () => {
    const match: RoutingRule["match"] = {};
    if (entity === "contact") {
      if (city.trim()) match.city = city.trim();
      if (source.trim()) match.source = source.trim();
    } else {
      if (serviceType.trim()) match.service_type = serviceType.trim();
      if (Number(minAmount) > 0) match.min_amount = Number(minAmount);
    }
    if (mode === "user" && !userId) {
      notify("Choose who the rule assigns to", { type: "warning" });
      return;
    }
    if (mode === "round_robin" && pool.length === 0) {
      notify("Pick at least one person for the rotation", {
        type: "warning",
      });
      return;
    }
    create(
      "lead_routing_rules",
      {
        data: {
          workspace_id: getActiveWorkspaceId(),
          entity,
          match,
          assign_mode: mode,
          assigned_to: mode === "user" ? Number(userId) : null,
          pool: mode === "round_robin" ? pool : [],
          position: (rules ?? []).length,
        },
      },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          setCity("");
          setSource("");
          setServiceType("");
          setMinAmount("");
          setPool([]);
          setUserId("");
          refresh();
        },
        onError: (error: unknown) =>
          notify(
            error instanceof Error ? error.message : "Could not add the rule",
            { type: "error" },
          ),
      },
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[12.5px] text-muted-foreground">
        When a new contact or deal arrives without an owner, the first matching
        rule assigns one. An explicit owner always wins over routing. Applied in
        the database, so forms, imports and the API all route the same way.
      </p>

      <div className="flex flex-col gap-1">
        {(rules ?? []).map((rule) => (
          <div
            key={rule.id}
            className="flex flex-wrap items-center gap-2 rounded-md border border-border-subtle px-2 py-1.5 text-[12.5px]"
          >
            <span className="font-medium capitalize">{rule.entity}s</span>
            <span className="text-muted-foreground">
              where {matchSummary(rule)}
            </span>
            <span>
              {rule.assign_mode === "user"
                ? `go to ${userName(rule.assigned_to)}`
                : `rotate among ${rule.pool.map(userName).join(", ")}`}
            </span>
            <label className="ml-auto flex items-center gap-1 text-[11.5px] text-muted-foreground">
              <input
                type="checkbox"
                checked={rule.active}
                onChange={() =>
                  update(
                    "lead_routing_rules",
                    {
                      id: rule.id,
                      data: { active: !rule.active },
                      previousData: rule,
                    },
                    { mutationMode: "pessimistic", onSuccess: () => refresh() },
                  )
                }
              />
              Active
            </label>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 text-muted-foreground hover:text-destructive"
              onClick={() =>
                deleteOne(
                  "lead_routing_rules",
                  { id: rule.id, previousData: rule },
                  { mutationMode: "pessimistic", onSuccess: () => refresh() },
                )
              }
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        ))}
        {(rules ?? []).length === 0 ? (
          <span className="text-[12.5px] text-muted-foreground">
            No rules yet; new unowned leads stay unassigned.
          </span>
        ) : null}
      </div>

      <div className="flex flex-col gap-2 rounded-md border border-dashed border-border p-3">
        <div className="flex flex-wrap items-center gap-2">
          <select
            className={inputClass}
            value={entity}
            onChange={(e) => setEntity(e.target.value as "contact" | "deal")}
          >
            <option value="contact">Contacts</option>
            <option value="deal">Deals</option>
          </select>
          {entity === "contact" ? (
            <>
              <input
                className={cn(inputClass, "w-32")}
                placeholder="City (optional)"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
              <input
                className={cn(inputClass, "w-32")}
                placeholder="Source (optional)"
                value={source}
                onChange={(e) => setSource(e.target.value)}
              />
            </>
          ) : (
            <>
              <input
                className={cn(inputClass, "w-32")}
                placeholder="Service (optional)"
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
              />
              <input
                className={cn(inputClass, "w-32")}
                type="number"
                placeholder="Min $ (optional)"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
              />
            </>
          )}
          <select
            className={inputClass}
            value={mode}
            onChange={(e) => setMode(e.target.value as "user" | "round_robin")}
          >
            <option value="round_robin">Round robin</option>
            <option value="user">One person</option>
          </select>
          {mode === "user" ? (
            <select
              className={inputClass}
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            >
              <option value="">Assign to…</option>
              {(users ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}
                </option>
              ))}
            </select>
          ) : null}
        </div>
        {mode === "round_robin" ? (
          <div className="flex flex-wrap gap-2 text-[12px]">
            {(users ?? []).map((u) => (
              <label key={u.id} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={pool.includes(u.id)}
                  onChange={() =>
                    setPool((prev) =>
                      prev.includes(u.id)
                        ? prev.filter((id) => id !== u.id)
                        : [...prev, u.id],
                    )
                  }
                />
                {u.first_name} {u.last_name}
              </label>
            ))}
          </div>
        ) : null}
        <Button size="sm" className="w-fit" onClick={addRule}>
          <Plus className="mr-1 size-3.5" />
          Add rule
        </Button>
      </div>
    </div>
  );
};
